// ============================================================
// frontend/src/pages/Produits.js  (renommé "Catalogue Produits & Services")
// Admin  : CRUD complet — colonnes réelles : nom, code_prestation (auto), description,
//          prix_unitaire, tva_applicable, unite, categorie, actif
// Client : Catalogue visuel + "Commander ce service" (devis direct) + "Demander un devis libre" (/demandes)
// ============================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import theme from '../theme';
import { useIsMobile } from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CATEGORIES = [
  { key: 'tous',           label: 'Tout voir',       emoji: '🔍' },
  { key: 'photovoltaique', label: 'Photovoltaïque',  emoji: '☀️' },
  { key: 'electricite',    label: 'Électricité',     emoji: '⚡' },
  { key: 'plomberie',      label: 'Plomberie',       emoji: '🔧' },
  { key: 'renovation',     label: 'Rénovation',      emoji: '🏠' },
  { key: 'chauffage',      label: 'Chauffage',       emoji: '🔥' },
  { key: 'autre',          label: 'Autre',           emoji: '📦' },
];

const UNITES = ['unité', 'heure', 'jour', 'forfait', 'm²', 'ml', 'kWc', 'kWh'];

const S = {
  btn: {
    border: 'none', borderRadius: '7px', cursor: 'pointer',
    fontWeight: '600', fontSize: '13px', padding: '8px 16px',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    transition: 'opacity .15s',
  },
  input: {
    width: '100%', padding: '9px 12px',
    border: `1px solid ${theme.border}`, borderRadius: '8px',
    fontSize: '13px', boxSizing: 'border-box', outline: 'none',
    color: theme.textPrimary, background: '#fafafa',
  },
  label: {
    display: 'block', marginBottom: '5px',
    fontWeight: '600', fontSize: '12px', color: theme.textSecondary,
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'white', borderRadius: '14px', padding: '28px',
    maxWidth: '520px', width: '96%', maxHeight: '90vh',
    overflowY: 'auto', boxShadow: theme.shadowLg,
  },
};

// Champs du formulaire avec valeurs par défaut
const emptyForm = {
  nom: '', description: '', prix_unitaire: '',
  tva_applicable: '21', unite: 'unité', categorie: 'autre', actif: true,
};

export default function Produits() {
  const isMobile = useIsMobile();
  const user      = JSON.parse(localStorage.getItem('user') || '{}');
  const token     = localStorage.getItem('token');
  const config    = { headers: { Authorization: `Bearer ${token}` } };
  const isAdmin   = user.role === 'admin';
  const navigate   = useNavigate();

  const [produits,      setProduits]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [catFiltre,     setCatFiltre]     = useState('tous');
  const [search,        setSearch]        = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editTarget,    setEditTarget]    = useState(null);
  const [form,          setForm]          = useState(emptyForm);
  const [saving,        setSaving]        = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error,         setError]         = useState('');
  // Filtre actif/inactif (admin uniquement)
  const [filtreActif,   setFiltreActif]   = useState('actif'); // 'actif' | 'inactif' | 'tous'
  // Modal "Commander" client (inline, sans redirection)
  const [commanderModal, setCommanderModal] = useState(null); // produit sélectionné ou null
  const [commanderForm,  setCommanderForm]  = useState({ description_besoin: '', date_souhaitee: '', note: '' });
  const [commanderSaving, setCommanderSaving] = useState(false);
  const [commanderOk,    setCommanderOk]    = useState(false);

  // ── Chargement ──────────────────────────────────────────────────────────────
  const fetchProduits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/prestations`, config);
      setProduits(res.data.data || res.data || []);
    } catch (e) {
      console.error('Erreur chargement catalogue:', e);
      setProduits([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchProduits();
  }, []); // eslint-disable-line

  // ── Filtrage ────────────────────────────────────────────────────────────────
  const filtered = produits.filter(p => {
    // Filtre actif/inactif (admin) : clients voient toujours seulement les actifs
    const actifOK = !isAdmin
      ? p.actif !== false
      : filtreActif === 'tous'
        ? true
        : filtreActif === 'actif'
          ? p.actif !== false
          : p.actif === false;
    const catOK = catFiltre === 'tous' || p.categorie === catFiltre;
    const q = search.toLowerCase().trim();
    if (!q) return actifOK && catOK;
    const hay = [p.nom, p.description, p.code_prestation, p.categorie, p.unite]
      .filter(Boolean).join(' ').toLowerCase();
    return actifOK && catOK && hay.includes(q);
  });

  // ── Modal créer ─────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  // ── Modal modifier ──────────────────────────────────────────────────────────
  const openEdit = (p) => {
    setEditTarget(p);
    setForm({
      nom:            p.nom            || '',
      description:    p.description    || '',
      prix_unitaire:  p.prix_unitaire  ?? '',
      tva_applicable: p.tva_applicable ?? '21',
      unite:          p.unite          || 'unité',
      categorie:      p.categorie      || 'autre',
      actif:          p.actif !== false,
    });
    setError('');
    setShowModal(true);
  };

  // ── Sauvegarder ─────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return setError('Le nom est obligatoire.');
    setSaving(true); setError('');
    try {
      const payload = {
        nom:            form.nom.trim(),
        description:    form.description.trim() || null,
        prix_unitaire:  parseFloat(form.prix_unitaire) || 0,
        tva_applicable: parseFloat(form.tva_applicable) || 21,
        unite:          form.unite,
        categorie:      form.categorie,
        actif:          form.actif,
      };
      if (editTarget) {
        await axios.put(`${API_URL}/prestations/${editTarget.id_prestation}`, payload, config);
      } else {
        await axios.post(`${API_URL}/prestations`, payload, config);
      }
      setShowModal(false);
      fetchProduits();
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  // ── Supprimer ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/prestations/${id}`, config);
      setDeleteConfirm(null);
      fetchProduits();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur suppression.');
    }
  };

  // ── Toggle actif ─────────────────────────────────────────────────────────────
  const toggleActif = async (p) => {
    try {
      await axios.put(`${API_URL}/prestations/${p.id_prestation}`, { actif: !p.actif }, config);
      fetchProduits();
    } catch { alert('Erreur modification statut.'); }
  };

  // ── Commander (client) — modal inline ───────────────────────────────────────
  // ── Demande de devis libre (client) → /demandes avec pré-remplissage ──
  const demanderDevis = (p) => {
    navigate('/demandes', { state: { type_prestation: p.nom, produit: p } });
  };

  const ouvrirCommander = (p) => {
    setCommanderModal(p);
    setCommanderForm({ description_besoin: '', date_souhaitee: '', note: '' });
    setCommanderOk(false);
    setCommanderError('');
  };

  const [commanderError, setCommanderError] = useState('');

  const envoyerCommande = async () => {
    setCommanderError('');
    if (!commanderForm.description_besoin.trim()) {
      setCommanderError('Merci de décrire votre besoin.'); return;
    }
    setCommanderSaving(true);
    try {
      const p   = commanderModal;
      const pu  = parseFloat(p.prix_unitaire) || 0;
      const tva = parseFloat(p.tva_applicable) || 6;
      const ht  = parseFloat((pu).toFixed(2));
      const tvaM = parseFloat((ht * tva / 100).toFixed(2));
      const payload = {
        montant_ht:  ht,
        montant_tva: tvaM,
        montant_ttc: parseFloat((ht + tvaM).toFixed(2)),
        notes: [
          `Commande : ${p.nom}`,
          commanderForm.description_besoin,
          commanderForm.date_souhaitee ? `Date souhaitée : ${commanderForm.date_souhaitee}` : ''
        ].filter(Boolean).join('\n'),
        lignes: [{
          description_personnalisee: p.nom
            + (commanderForm.description_besoin ? ' — ' + commanderForm.description_besoin : ''),
          quantite:      1,
          prix_unitaire: pu,
          tva,
          id_prestation: p.id_prestation || null,
        }],
      };
      const res = await axios.post(`${API_URL}/devis`, payload, config);
      if (res.data?.success) {
        setCommanderOk(true);
      } else {
        setCommanderError(res.data?.message || 'Erreur inattendue. Réessayez.');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Erreur réseau. Réessayez.';
      setCommanderError(msg);
    } finally {
      setCommanderSaving(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: theme.pageBg }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ background: theme.gradientHeader, padding: '20px 24px', color: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto',
          display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
          flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>
              🛠️ Catalogue Produits &amp; Services
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
              {isAdmin
                ? `${produits.length} article${produits.length !== 1 ? 's' : ''} · ${produits.filter(p => p.actif !== false).length} actifs`
                : `${filtered.length} produit${filtered.length !== 1 ? 's' : ''} disponible${filtered.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {isAdmin && (
            <button onClick={openCreate}
              style={{ ...S.btn, background: 'white', color: theme.primary, padding: '10px 20px' }}>
              ➕ Nouveau produit / service
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '14px 12px' : '24px' }}>

        {/* ── Filtres ──────────────────────────────────────────────────────── */}
        <div style={{
          background: 'white', borderRadius: '12px', padding: isMobile ? '10px 12px' : '12px 16px',
          boxShadow: theme.shadow, marginBottom: '20px',
          display: 'flex', flexDirection: 'column', gap: '8px',
        }}>
          {/* Recherche — pleine largeur */}
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Rechercher…"
            style={{ ...S.input, marginBottom: 0, width: '100%', boxSizing: 'border-box' }} />

          {/* Catégories — scroll horizontal sur mobile */}
          <div style={{
            display: 'flex', gap: '6px',
            overflowX: isMobile ? 'auto' : 'visible',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
          }}>
            {CATEGORIES.map(c => (
              <button key={c.key} onClick={() => setCatFiltre(c.key)} style={{
                ...S.btn, padding: '6px 13px', fontSize: '12px', flexShrink: 0,
                background: catFiltre === c.key ? theme.primary : '#f0f2f5',
                color:      catFiltre === c.key ? 'white'        : theme.textSecondary,
              }}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>

          {/* Filtres admin — scroll horizontal sur mobile */}
          {isAdmin && (
            <div style={{
              display: 'flex', gap: '4px',
              overflowX: isMobile ? 'auto' : 'visible',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
            }}>
              {[
                { key: 'actif',   label: '✅ Actifs'   },
                { key: 'inactif', label: '🗃️ Archivés'  },
                { key: 'tous',    label: '📋 Tous'      },
              ].map(f => (
                <button key={f.key} onClick={() => setFiltreActif(f.key)} style={{
                  ...S.btn, padding: '5px 11px', fontSize: '12px', flexShrink: 0,
                  background: filtreActif === f.key ? (f.key === 'inactif' ? '#c62828' : theme.primary) : '#f0f2f5',
                  color:      filtreActif === f.key ? 'white' : theme.textSecondary,
                }}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Contenu ──────────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted }}>
            ⏳ Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', background: 'white',
            borderRadius: '12px', boxShadow: theme.shadow }}>
            <p style={{ fontSize: '40px', margin: '0 0 12px' }}>📦</p>
            <p style={{ color: theme.textMuted }}>
              {search || catFiltre !== 'tous'
                ? 'Aucun produit ne correspond à votre recherche.'
                : isAdmin ? 'Catalogue vide — créez le premier article !' : 'Aucun produit disponible.'}
            </p>
            {isAdmin && !search && catFiltre === 'tous' && (
              <button onClick={openCreate}
                style={{ ...S.btn, background: theme.primary, color: 'white', marginTop: '12px' }}>
                ➕ Créer un produit
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '18px' }}>
            {filtered.map(p => {
              const catInfo = CATEGORIES.find(c => c.key === p.categorie) || CATEGORIES[CATEGORIES.length - 1];
              const actif   = p.actif !== false;
              return (
                <div key={p.id_prestation} style={{
                  background: 'white', borderRadius: '14px', overflow: 'hidden',
                  boxShadow: theme.shadow, opacity: actif ? 1 : 0.55,
                  border: `1px solid ${theme.border}`,
                  display: 'flex', flexDirection: 'column',
                }}>
                  {/* Bandeau catégorie */}
                  <div style={{
                    background: theme.gradientHeader, padding: '12px 16px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{catInfo.emoji}</span>
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)',
                        fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {catInfo.label}
                      </span>
                    </div>
                    {isAdmin && (
                      <button onClick={() => toggleActif(p)}
                        style={{ ...S.btn, padding: '3px 8px', fontSize: '10px',
                          background: actif ? theme.successBg : '#f5f5f5',
                          color: actif ? theme.success : theme.textMuted }}>
                        {actif ? '✅ Actif' : '⏸ Inactif'}
                      </button>
                    )}
                  </div>

                  {/* Corps */}
                  <div style={{ padding: '16px', flex: 1,
                    display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700',
                      color: theme.textPrimary }}>
                      {p.nom}
                    </h3>
                    {p.code_prestation && (
                      <span style={{ fontSize: '10px', color: theme.textMuted,
                        background: theme.primaryBg, padding: '2px 7px', borderRadius: '10px',
                        display: 'inline-block', width: 'fit-content' }}>
                        {p.code_prestation}
                      </span>
                    )}
                    {p.description && (
                      <p style={{ margin: 0, fontSize: '13px', color: theme.textSecondary,
                        lineHeight: 1.5 }}>
                        {p.description}
                      </p>
                    )}
                    <div style={{ marginTop: 'auto', paddingTop: '10px',
                      borderTop: `1px solid ${theme.border}`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: theme.primary }}>
                        {parseFloat(p.prix_unitaire || 0).toFixed(2)} €
                        <span style={{ fontSize: '11px', fontWeight: '400', color: theme.textMuted }}>
                          {' '}/{' '}{p.unite || 'unité'}
                        </span>
                      </span>
                      <span style={{ fontSize: '11px', color: theme.textMuted }}>
                        TVA {p.tva_applicable ?? 21}%
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ padding: '10px 16px', borderTop: `1px solid ${theme.border}`,
                    display: 'flex', gap: '8px' }}>
                    {isAdmin ? (
                      <>
                        <button onClick={() => openEdit(p)}
                          style={{ ...S.btn, flex: 1, background: theme.primaryBg,
                            color: theme.primary, justifyContent: 'center' }}>
                          ✏️ Modifier
                        </button>
                        {/* Archiver / Restaurer selon statut actif */}
                        {actif ? (
                          <button onClick={() => toggleActif(p)}
                            title="Archiver ce produit (le rend invisible pour les clients)"
                            style={{ ...S.btn, background: '#fff3e0', color: '#e65100', padding: '8px 12px' }}>
                            📦 Archiver
                          </button>
                        ) : (
                          <button onClick={() => toggleActif(p)}
                            title="Restaurer ce produit (le rend visible pour les clients)"
                            style={{ ...S.btn, background: theme.successBg, color: theme.success, padding: '8px 12px' }}>
                            🔄 Restaurer
                          </button>
                        )}
                      </>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {/* Bouton 1: Commander le service (prix fixe admin) */}
                        <button onClick={() => ouvrirCommander(p)} disabled={!actif}
                          style={{ ...S.btn, justifyContent: 'center',
                            background: actif ? theme.primary : '#ccc', color: 'white',
                            padding: '10px', cursor: actif ? 'pointer' : 'not-allowed' }}>
                          🛒 Commander ce service
                        </button>
                        {/* Bouton 2: Demande de devis libre */}
                        <button onClick={() => demanderDevis(p)} disabled={!actif}
                          style={{ ...S.btn, justifyContent: 'center',
                            background: actif ? '#fff' : '#f5f5f5',
                            color: actif ? theme.primary : '#aaa',
                            border: `1px solid ${actif ? theme.primary : '#ddd'}`,
                            padding: '9px', cursor: actif ? 'pointer' : 'not-allowed' }}>
                          📋 Demander un devis libre
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Modal CRUD ──────────────────────────────────────────────────────── */}
      {showModal && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={S.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: theme.textPrimary }}>
                {editTarget ? '✏️ Modifier' : '➕ Nouveau produit / service'}
              </h2>
              <button onClick={() => setShowModal(false)}
                style={{ ...S.btn, background: 'transparent', color: '#999', fontSize: '20px', padding: 0 }}>
                ×
              </button>
            </div>

            <form onSubmit={handleSave}>
              {error && (
                <div style={{ background: theme.dangerBg, color: theme.danger,
                  padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px' }}>
                  ⚠️ {error}
                </div>
              )}

              <div style={{ display: 'grid', gap: '14px' }}>
                <div>
                  <label style={S.label}>Nom *</label>
                  <input style={S.input} value={form.nom}
                    onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                    placeholder="Ex : Installation panneau solaire 400Wc" />
                </div>

                <div>
                  <label style={S.label}>Description</label>
                  <textarea rows={3}
                    style={{ ...S.input, height: 'auto', resize: 'vertical' }}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Description détaillée…" />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={S.label}>Prix HTVA (€)</label>
                    <input type="number" min="0" step="0.01" style={S.input}
                      value={form.prix_unitaire}
                      onChange={e => setForm(f => ({ ...f, prix_unitaire: e.target.value }))}
                      placeholder="0.00" />
                  </div>
                  <div>
                    <label style={S.label}>TVA (%)</label>
                    <input type="number" min="0" max="100" step="1" style={S.input}
                      value={form.tva_applicable}
                      onChange={e => setForm(f => ({ ...f, tva_applicable: e.target.value }))}
                      placeholder="21" />
                  </div>
                  <div>
                    <label style={S.label}>Unité</label>
                    <select style={S.input} value={form.unite}
                      onChange={e => setForm(f => ({ ...f, unite: e.target.value }))}>
                      {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label style={S.label}>Catégorie</label>
                  <select style={S.input} value={form.categorie}
                    onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                    {CATEGORIES.filter(c => c.key !== 'tous').map(c => (
                      <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>
                    ))}
                  </select>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '10px',
                  cursor: 'pointer', fontSize: '13px', color: theme.textPrimary }}>
                  <input type="checkbox" checked={form.actif}
                    onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))} />
                  Produit actif (visible dans le catalogue client)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" disabled={saving}
                  style={{ ...S.btn, flex: 1, background: theme.primary, color: 'white',
                    justifyContent: 'center', padding: '11px' }}>
                  {saving ? '⏳ Enregistrement…' : editTarget ? '💾 Enregistrer' : '✅ Créer'}
                </button>
                <button type="button" onClick={() => setShowModal(false)}
                  style={{ ...S.btn, background: '#f0f0f0', color: theme.textSecondary,
                    padding: '11px 18px' }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal suppression ──────────────────────────────────────────────── */}
      {deleteConfirm && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div style={{ ...S.modal, maxWidth: '360px', textAlign: 'center' }}>
            <p style={{ fontSize: '36px', margin: '0 0 10px' }}>🗑️</p>
            <h3 style={{ margin: '0 0 8px', color: theme.textPrimary }}>Confirmer la suppression</h3>
            <p style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '18px' }}>
              Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => handleDelete(deleteConfirm)}
                style={{ ...S.btn, background: theme.danger, color: 'white', padding: '9px 22px' }}>
                🗑️ Supprimer
              </button>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ ...S.btn, background: '#f0f0f0', color: theme.textSecondary, padding: '9px 18px' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── MODAL COMMANDER (client inline) ─────────────────────────────── */}
      {commanderModal && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setCommanderModal(null)}>
          <div style={{ ...S.modal, maxWidth: '480px' }}>
            {commanderOk ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <h3 style={{ margin: '0 0 10px', color: theme.success }}>Commande enregistrée !</h3>
                <p style={{ color: theme.textSecondary, fontSize: '14px', marginBottom: '20px' }}>
                  Un devis brouillon a été créé pour <strong>{commanderModal.nom}</strong>.<br/>
                  Notre équipe va le compléter et vous l'envoyer rapidement pour validation.
                </p>
                <button onClick={() => setCommanderModal(null)}
                  style={{ ...S.btn, background: theme.primary, color: 'white', padding: '10px 24px' }}>
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ margin: 0, fontSize: '17px', color: theme.textPrimary }}>
                    🛒 {commanderModal.nom}
                  </h2>
                  <button onClick={() => setCommanderModal(null)}
                    style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}>
                    ✕
                  </button>
                </div>
                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: theme.textSecondary }}>
                  {commanderModal.description && <p style={{ margin: '0 0 4px' }}>{commanderModal.description}</p>}
                  <strong style={{ color: theme.primary }}>{parseFloat(commanderModal.prix_unitaire || 0).toFixed(2)} € / {commanderModal.unite || 'unité'}</strong>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={S.label}>📝 Décrivez votre besoin *</label>
                    <textarea
                      value={commanderForm.description_besoin}
                      onChange={e => setCommanderForm(f => ({ ...f, description_besoin: e.target.value }))}
                      placeholder="Décrivez votre projet, les quantités souhaitées, vos contraintes…"
                      rows={4}
                      style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                  <div>
                    <label style={S.label}>📅 Date d'intervention souhaitée (facultatif)</label>
                    <input
                      type="date"
                      value={commanderForm.date_souhaitee}
                      onChange={e => setCommanderForm(f => ({ ...f, date_souhaitee: e.target.value }))}
                      style={S.input}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                  <button onClick={() => setCommanderModal(null)}
                    style={{ ...S.btn, background: '#f0f0f0', color: theme.textSecondary, padding: '10px 18px' }}>
                    Annuler
                  </button>
                  {commanderError && (
                    <div style={{ background:'#fdecea',border:'1px solid #e57373',borderRadius:'8px',
                      padding:'10px 14px',fontSize:'13px',color:'#c62828',marginBottom:'12px' }}>
                      ❌ {commanderError}
                    </div>
                  )}
                  <button onClick={envoyerCommande} disabled={commanderSaving}
                    style={{ ...S.btn, background: theme.primary, color: 'white', padding: '10px 22px', opacity: commanderSaving ? 0.7 : 1 }}>
                    {commanderSaving ? '⏳ Envoi…' : '📤 Envoyer ma demande'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
